import { PublicProfile } from '@soniq/util-soniqstudio-wasm'
import { AppError } from '@soniq/public-resource'
import { useAsyncSingleResponse } from '@soniq/public-manager'
import { fetchOwnPublicProfile } from '@soniq/public-resource-connection'
import { useAuthContext, useAuthUserId } from '@soniq/util-auth'
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useBasicResourceOptions } from '~m/AppUi/context/hooks.ts'

export interface OwnPublicProfile {
  /**
   * `null` when the user has no profile yet (part-way through
   * sign-up). `undefined` when nothing has been loaded.
   */
  value: PublicProfile | null | undefined
  loading: boolean
  /**
   * Set when the last fetch failed. `value` still holds the last
   * loaded profile, if there was one.
   */
  error: Error | undefined
  /**
   * Read the public profile from the API again. Resolves with the
   * profile now showing; a failure is reported through `error`.
   */
  refetch: () => Promise<PublicProfile | null>
  /** Show a change the user just saved, before the API has it. */
  patch: (changes: Partial<PublicProfile>) => void
}

const emptyOwnPublicProfile: OwnPublicProfile = {
  value: undefined,
  loading: false,
  error: undefined,
  refetch: async () => null,
  patch: () => {},
}

const OwnPublicProfileContext = createContext<OwnPublicProfile>(
  emptyOwnPublicProfile
)

/**
 * The answer of one fetch, tagged with the user it was fetched for.
 * Anything tagged with another user is ignored, so nothing from a
 * previous user can show up for the next one.
 */
interface FetchedOwnPublicProfile {
  userId: string
  /**
   * `null` when the user has no profile yet. `undefined` when the
   * fetch failed and no profile had been loaded for this user.
   */
  profile: PublicProfile | null | undefined
  error: Error | undefined
}

interface OwnPublicProfilePatch {
  userId: string | undefined
  changes: Partial<PublicProfile> | undefined
}

/**
 * Holds the signed-in user's own public profile, so every consumer
 * shares one request. Needs no database.
 *
 * Mounted once for the whole app, inside the router. It must not
 * remount its children when the signed-in user changes, so the
 * fetched profile and the patch are each tagged with their user id
 * instead of living in a component keyed by it.
 */
export function OwnPublicProfileProvider({
  children,
}: PropsWithChildren) {
  const { isAuthenticated } = useAuthContext()
  const authUserId = useAuthUserId()
  const userId = isAuthenticated ? authUserId : undefined
  const options = useBasicResourceOptions()
  const { apiBaseUrl, accessToken } = options
  const enabled = Boolean(userId && accessToken)
  const { pathname } = useLocation()

  // The last profile the API returned, kept so a failed refetch
  // does not blank a profile that is already showing.
  const lastLoaded = useRef<{
    userId: string
    profile: PublicProfile | null
  }>(undefined)

  // The fetch never rejects. A failure is part of its answer, so the
  // error carries the same user tag as the profile.
  const fetched =
    useAsyncSingleResponse<FetchedOwnPublicProfile>(
      async () => {
        if (!userId || !accessToken) {
          return null
        }
        try {
          const response = await fetchOwnPublicProfile(
            { apiBaseUrl, accessToken },
            userId
          )
          const profile = response?.data ?? null
          lastLoaded.current = { userId, profile }
          return { data: { userId, profile, error: undefined } }
        } catch (error) {
          // The profile does not exist yet: the user is part-way
          // through sign-up. That is an answer, not a failure.
          if (
            error instanceof AppError &&
            error.status === 404
          ) {
            lastLoaded.current = { userId, profile: null }
            return {
              data: { userId, profile: null, error: undefined },
            }
          }
          return {
            data: {
              userId,
              profile:
                lastLoaded.current?.userId === userId
                  ? lastLoaded.current.profile
                  : undefined,
              error:
                error instanceof Error
                  ? error
                  : new Error(String(error)),
            },
          }
        }
      },
      [apiBaseUrl, accessToken, userId],
      // The shared hook only fetches by itself when `enabled` changes.
      // A change of user needs a fetch too, so every fetch is started
      // from here.
      { enabled, refetchOnly: true }
    )

  const settled =
    enabled && fetched.value?.userId === userId
      ? fetched.value
      : undefined
  const profile = settled?.profile
  const error = settled?.error

  // An enabled provider is loading from its first render until a
  // fetch for this user has settled (a profile, no profile yet, or
  // an error).
  const loading = enabled && (fetched.loading || !settled)

  // Fetch again while this user has no loaded profile: they may have
  // been offline, or have just signed up, in which case the profile
  // reaches the API only after sync.
  const shouldRetry =
    enabled && Boolean(settled) && !profile && !fetched.loading

  // Effects below call the latest `refetch` (it is rebuilt for a new
  // access token) without running again because it changed.
  const latest = useRef({
    refetch: fetched.refetch,
    shouldRetry,
  })
  useEffect(() => {
    latest.current = { refetch: fetched.refetch, shouldRetry }
  })

  // A new access token for the same user does not fetch again.
  useEffect(() => {
    if (enabled) {
      void latest.current.refetch()
    }
  }, [enabled, userId])

  useEffect(() => {
    if (latest.current.shouldRetry) {
      void latest.current.refetch()
    }
  }, [pathname])

  useEffect(() => {
    function retry() {
      if (latest.current.shouldRetry) {
        void latest.current.refetch()
      }
    }
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [])

  // The patch belongs to one user. It is dropped as soon as the user
  // id changes, so it cannot come back if that user signs in again.
  const [patched, setPatched] = useState<OwnPublicProfilePatch>({
    userId,
    changes: undefined,
  })
  if (patched.userId !== userId) {
    setPatched({ userId, changes: undefined })
  }
  const changes =
    patched.userId === userId ? patched.changes : undefined

  const patch = useCallback((next: Partial<PublicProfile>) => {
    setPatched((previous) => ({
      ...previous,
      changes: { ...previous.changes, ...next },
    }))
  }, [])

  const refetch = useCallback(async () => {
    const answer = await latest.current.refetch()
    return answer?.profile ?? null
  }, [])

  const value = useMemo(
    () =>
      profile && changes ? { ...profile, ...changes } : profile,
    [profile, changes]
  )

  const own = useMemo<OwnPublicProfile>(
    () => ({ value, loading, error, refetch, patch }),
    [value, loading, error, refetch, patch]
  )

  return (
    <OwnPublicProfileContext.Provider value={own}>
      {children}
    </OwnPublicProfileContext.Provider>
  )
}

export function useOwnPublicProfile(): OwnPublicProfile {
  return useContext(OwnPublicProfileContext)
}
